import { useCallback, useState } from 'react';
import { components } from '@api/play-account/generated';
import { useLocation } from '@tanstack/react-router';

import { useAuth } from '@features/auth/hooks/useAuth';
import { getGamePgnLink } from '@features/shared/api/streams';
import { ClockControlType } from '@features/shared/api/typings';
import {
  usePlayerEloStatsQuery,
  usePlayerGamesHistoryQuery,
  usePlayerGamesStatsQuery,
} from '@features/user/api/hooks';
import { useGoToGame } from '@features/user/hooks/useGoToGame';
import { PlayerGamesListFull } from '@features/user/ui/PlayerGamesListFull/PlayerGamesListFull';
import { PlayerShieldQuickPeekWidget } from '@features/user/ui/PlayerShieldQuickPeek/PlayerShieldQuickPeekWidget';

type Props = {
  isOpen: boolean;
  onClose: () => void;
  clockControlType: ClockControlType;
};
export function PlayerGamesListFullWidget({ isOpen, onClose, clockControlType }: Props) {
  const [dateRange, setDateRange] = useState<{
    start: Date | null;
    end: Date | null;
  }>({ start: null, end: new Date() });
  const { user } = useAuth();

  const { data: stats } = usePlayerEloStatsQuery({
    enabled: isOpen,
    userId: user?.userId,
  });

  const {
    data: games,
    fetchNextPage,
    hasNextPage,
  } = usePlayerGamesHistoryQuery({
    enabled: isOpen,
    clockControlType,
    userId: user?.userId,
    start: dateRange.start,
    end: dateRange.end,
    limit: 20,
  });

  const { data: gamesStats } = usePlayerGamesStatsQuery({
    enabled: isOpen,
    clockControlType,
    userId: user?.userId,
    start: dateRange.start,
    end: dateRange.end,
  });

  // TODO вынести в хелпер генерации урл, и переделать на href
  const onDownloadPgn = useCallback((gameId: string) => {
    window.open(getGamePgnLink(gameId), '_blank');
  }, []);

  const onGameClick = useGoToGame(useLocation().pathname);

  return (
    <PlayerGamesListFull
      isOpen={isOpen}
      onClose={onClose}
      games={games}
      fetchNextPage={fetchNextPage}
      hasNextPage={hasNextPage}
      clockControlType={clockControlType}
      rank={getStats(clockControlType, stats)?.rating}
      gameCount={gamesStats?.gamesCount}
      dateRange={dateRange}
      onDateRangeChange={setDateRange}
      onDownloadPgn={onDownloadPgn}
      onGameClick={onGameClick}
      renderShieldQuickPeek={(userId) => <PlayerShieldQuickPeekWidget userId={userId} />}
      opponentAvgRank={gamesStats?.averageOpponentElo}
    />
  );
}

function getStats(
  clockControlType: ClockControlType,
  eloStats?: components['schemas']['UserEloDto'],
): components['schemas']['EloDto'] | undefined {
  if (clockControlType === 'Bullet') return eloStats?.bullet;
  if (clockControlType === 'Blitz') return eloStats?.blitz;
  if (clockControlType === 'Rapid') return eloStats?.rapid;
  if (clockControlType === 'Classic') return eloStats?.classic;
}
